import React, { useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, X, Copy, Loader2, Loader } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
// import { format } from "date-fns";
import { useApproveDeposit, useDeclineDeposit, useDeposit, useSendManualReviewEmail } from "../hooks/useDeposit";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DeclineReasonModal from "../../../components/ui/DeclineReasonModal";
import ApproveDepositModal from "../../../components/ui/ApproveDepositModal";

// details modal
const DetailsModal = ({ isOpen, onClose, data, onApprove, onReject, onSendReviewEmail, isUsdVirtualAccount }) => {
  if (!isOpen || !data) return null;

  const detailItem = (label, value) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value || "N/A"}</span>
    </div>
  );

  const formatNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const symbol = data.currency?.symbol || data.wallet?.currency?.symbol || "";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden h-[90vh] mx-4 w-full max-w-2xl bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Deposit Details</h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[80%] overflow-y-auto">
          <div className="grid grid-cols-1 gap-y-2 gap-x-8 md:grid-cols-2">
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Transaction Information</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detailItem("Transaction ID", data.uuid)}
                {detailItem("Reference", data.providerReference || data.reference)}
                {detailItem("Gateway Reference", data.paymentGatewayReference)}
                {detailItem("Status", data.status?.replace("_", " ").toUpperCase())}
                {detailItem("Date", data.createdAt || data.created ? new Date(data.createdAt || data.created).toLocaleString() : "N/A")}
                {detailItem("Amount", `${symbol} ${formatNumber(data.amount)}`)}
                {detailItem("Total Amount", `${symbol} ${formatNumber(data.totalAmount)}`)}
                {detailItem("Platform Fee", `${symbol} ${formatNumber(data.platformFee)}`)}
                {detailItem("Provider Fee", `${symbol} ${formatNumber(data.providerFee || data.paymentGatewayFee)}`)}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">User Details</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detailItem("Full Name", `${data.user?.firstName || ""} ${data.user?.lastName || ""}`)}
                {detailItem("Email", data.user?.email)}
                {detailItem("Phone", data.user?.phoneNumber)}
                {detailItem("Country", data.user?.country)}
                {detailItem("KYC Completed", data.user?.hasCompletedKYC ? "YES" : "NO")}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Currency Info</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {detailItem("Code", data.currency?.currencyCode || data.wallet?.currency?.currencyCode)}
                {detailItem("Symbol", data.currency?.symbol || data.wallet?.currency?.symbol)}
                {detailItem("Country", data.currency?.country || data.wallet?.currency?.country)}
              </div>
            </div>

            <div className="col-span-2 mt-1">
              <span className="block pb-2 mb-2 text-xs font-bold text-emerald-800 uppercase border-b border-emerald-100">Description</span>
              <p className="font-sans text-sm italic leading-relaxed text-gray-700">
                {data.description || "No description provided"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center px-2 py-6 space-x-2 space-y-0 bg-gray-50 border-t md:justify-end md:space-y-0 md:space-x-4">
          {isUsdVirtualAccount && (
            <button
              onClick={() => onSendReviewEmail(data)}
              className="p-2 font-bold text-blue-600 bg-white rounded-xl border-2 border-blue-500 shadow-sm transition-all md:py-3 md:px-8 hover:bg-blue-50 active:scale-95"
            >
              Send Review Email
            </button>
          )}

          <button
            onClick={() => onReject(data)}
            className="p-2 font-bold text-red-600 bg-white rounded-xl border-2 border-red-500 shadow-sm transition-all md:py-3 md:px-8 hover:bg-red-50 active:scale-95"
          >
            Decline Deposit
          </button>

          <button
            onClick={() => {
              onApprove(data);
              onClose();
            }}
            className="flex justify-center items-center p-2 font-bold text-white bg-emerald-600 rounded-xl shadow-lg transition-all md:py-3 md:px-8 hover:bg-emerald-700 active:scale-95"
          >
            Approve Deposit
          </button>
        </div>
      </div>
    </div>
  );
};

function DepositTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);

  const [loadingRowId, setLoadingRowId] = useState(null);

  const [reservePercentage, setReservePercentage] = useState("");
  const [otcPlatformPercentage, setOtcPlatformPercentage] = useState("");
  const [reserveHoldDays, setReserveHoldDays] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter]);

  const { data, isLoading, isError, error } = useDeposit(page, limit, debouncedFilter);
  const { mutate: approveDeposit, isPending: isApproving } = useApproveDeposit();
  const { mutate: declineDeposit, isPending: isDeclining } = useDeclineDeposit();
  const { mutate: sendManualReviewEmail } = useSendManualReviewEmail();


  const navigate = useNavigate();

  const handleViewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setIsDetailsModalOpen(true);
  };

  const handleOpenDecline = (deposit) => {
    setDeclineTarget(deposit);
    setIsDeclineModalOpen(true);
  };

  const handleOpenApprove = (deposit) => {
    setApproveTarget(deposit);
    setIsApproveModalOpen(true);
  };

  const handleConfirmDecline = (reason) => {
    if (!declineTarget) return;

    setLoadingRowId(declineTarget.uuid);
    declineDeposit(
      {
        transactionUuid: declineTarget?.uuid,
        reason: reason,
      },
      {
        onSuccess: () => {
          toast.success("Deposit declined successfully");
          setIsDeclineModalOpen(false);
          setIsDetailsModalOpen(false);
          setDeclineTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to decline deposit");
          setLoadingRowId(null);
        },
      }
    );
  };

  const handleConfirmApprove = (metadata) => {
    if (!approveTarget) return;

    setLoadingRowId(approveTarget.uuid);
    approveDeposit(
      {
        uuid: approveTarget?.uuid,
        // reference:  approveTarget?.reference, // approveTarget?.providerReference 
        reservePercentage: metadata.reservePercentage,
        otcPlatformPercentage: metadata.otcPlatformPercentage,
        reserveHoldDays: metadata.reserveHoldDays,
      },
      {
        onSuccess: () => {
          toast.success("Deposit approved successfully");
          setIsApproveModalOpen(false);
          setIsDetailsModalOpen(false);
          setReservePercentage("");
          setOtcPlatformPercentage("");
          setApproveTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to approve deposit");
          setLoadingRowId(null);
        },
      }
    );
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length
      ? `${text.slice(0, length)}...`
      : text;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // paymentGateway === 'cashramp' is the authoritative signal (set at deposit-creation
  // time on every USD virtual account webhook deposit). Older rows created before that
  // field existed fall back to the currency+serviceType heuristic, which is a weaker
  // proxy — it also matches non-Cashramp USD wallet funding (e.g. a card top-up).
  const isUsdVirtualAccountDeposit = (deposit) => {
    if (!deposit) return false;
    if (deposit.paymentGateway) return deposit.paymentGateway === "cashramp";
    return (
      deposit?.wallet?.currency?.currencyCode?.toLowerCase() === "usd" &&
      deposit?.serviceType === "wallet_funding"
    );
  };

  const handleSendReviewEmail = (deposit) => {
    setLoadingRowId(deposit.uuid);
    sendManualReviewEmail(deposit.uuid, {
      onSuccess: () => {
        toast.success("Review email sent to user");
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to send review email");
        setLoadingRowId(null);
      },
    });
  };

  const columns = [
    {
      accessorKey: "uuid",
      header: "Transaction ID",
      cell: ({ getValue }) => {
        const value = getValue();

        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>
              {truncateText(value, 12)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "providerReference",
      header: "Reference",
      cell: ({ row }) => {
        const value = row.original.reference; //  row.original.providerReference 

        return (
          <div className="flex gap-2 justify-between items-center min-w-[130px]">
            <span title={value}>
              {truncateText(value || "--", 12)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "paymentGatewayReference",
      header: "Gateway Reference",
      cell: ({ row }) => {
        const value = row.original.paymentGatewayReference;

        return (
          <div className="flex gap-2 justify-between items-center min-w-[150px]">
            <span title={value}>
              {truncateText(value, 15)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: (info) => {
        const val = info.row.original.createdAt || info.row.original.created;
        return val ? new Date(val).toLocaleString() : "N/A";
      }
    },
    {
      id: "user",
      header: "User",
      accessorFn: (row) => `${row.user?.firstName || ""} ${row.user?.lastName || ""}`,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {row.original.user?.firstName || "N/A"} {row.original.user?.lastName || ""}
          </span>
          <span className="text-xs text-gray-500">{row.original.user?.email}</span>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row) => {
        const symbol = row.currency?.symbol || row.wallet?.currency?.symbol || "";
        return `${symbol}${row.amount}`;
      },
      cell: ({ row }) => (
        <span className="font-semibold whitespace-nowrap">
          {row.original.currency?.symbol || row.original.wallet?.currency?.symbol || ""}{" "}
          {Number(row.original.amount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: "totalAmount",
      header: "Total Amount",
      accessorFn: (row) => {
        const symbol = row.currency?.symbol || row.wallet?.currency?.symbol || "";
        return `${symbol}${row.totalAmount}`;
      },
      cell: ({ row }) => (
        <span className="text-gray-600 whitespace-nowrap">
          {row.original.currency?.symbol || row.original.wallet?.currency?.symbol || ""} {Number(row.original.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      id: "serviceType",
      accessorKey: "serviceType",
      header: "Service Type",
      cell: (info) => (
        <span className="capitalize">{info.getValue()?.replace("_", " ")}</span>
      ),
    },
    {
      id: "depositType",
      header: "Deposit Type",
      cell: ({ row }) =>
        isUsdVirtualAccountDeposit(row.original) ? (
          <span className="px-3 py-1 text-xs font-medium text-blue-800 whitespace-nowrap bg-blue-100 rounded-full">
            USD VIRTUAL ACCOUNT
          </span>
        ) : (
          <span className="text-gray-400">--</span>
        ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();

        const statusStyles = {
          manual_review: "bg-yellow-100 text-yellow-800",
          approved: "bg-green-100 text-green-800",
          rejected: "bg-red-100 text-red-800",
          pending: "bg-blue-100 text-blue-800",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[status] || "bg-gray-100 text-gray-800"
              }`}
          >
            {status?.replace("_", " ").toUpperCase()}
          </span>
        );
      }
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const deposit = row.original;
        const isRowLoading = loadingRowId === deposit.uuid;

        const handleApprove = (deposit) => {
          handleOpenApprove(deposit);
        };

        const handleDecline = (deposit) => {
          handleOpenDecline(deposit);
        };

        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(deposit),
          },
          {
            label: "Approve",
            onClick: () => handleApprove(deposit),
            className: "text-green-600",
          },
          {
            label: "Decline",
            onClick: () => handleDecline(deposit),
            className: "text-red-600",
          },
          ...(isUsdVirtualAccountDeposit(deposit)
            ? [
                {
                  label: "Send Review Email",
                  onClick: () => handleSendReviewEmail(deposit),
                  className: "text-blue-600",
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            trigger={isRowLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
            items={dropdownItems}
          />
        );
      },
    },
  ];

  // The API paginates and searches server-side — render its page as-is.
  const records = React.useMemo(() => data?.data ?? [], [data]);
  const totalPages = data?.pagination?.totalPages || 1;
  const totalItems = data?.pagination?.total || 0;
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, totalItems);

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // if (isLoading) {
  //   return (
  //     <div className="flex h-screen">
  //       <div className="flex flex-1 justify-center items-center">
  //         <p className="font-medium text-gray-600">Loading deposits data...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex flex-1 justify-center items-center">
          <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
            Error: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-between items-center pb-5 bg-white">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ref, name or email..."
            className="py-3 pr-4 pl-12 w-full text-sm rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      {
        isLoading ? (
          <div className="flex h-[400px]">
            <div className="flex flex-1 justify-center items-center">
              <p className="flex gap-2 items-center font-medium text-gray-600">Loading deposits review data <Loader className="w-5 h-5 animate-spin" /></p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-gray-50/50">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6 ${header.column.columnDef.id === "serviceType" || header.column.columnDef.id === "totalAmount" ? "min-w-[140px]" : ""}`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-100 hover:border-b">
                  {records.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="transition-colors hover:bg-gray-50">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <p className="text-lg font-medium">No records matching your search</p>
                          <p className="text-sm">Try adjusting your filters or search terms</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{rangeStart}</span> to{" "}
                <span className="font-medium">{rangeEnd}</span>{" "}
                of <span className="font-medium">{totalItems}</span> entries
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )
      }

      <DetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        data={selectedDeposit}
        onApprove={handleOpenApprove}
        onReject={handleOpenDecline}
        onSendReviewEmail={handleSendReviewEmail}
        isUsdVirtualAccount={isUsdVirtualAccountDeposit(selectedDeposit)}
      />

      <DeclineReasonModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onSubmit={handleConfirmDecline}
        isLoading={isDeclining}
        title="Decline Deposit"
        data={declineTarget}
      />

      <ApproveDepositModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onSubmit={handleConfirmApprove}
        isLoading={isApproving}
        title="Approve Deposit"
        data={approveTarget}
        reservePercentage={reservePercentage}
        setReservePercentage={setReservePercentage}
        otcPlatformPercentage={otcPlatformPercentage}
        setOtcPlatformPercentage={setOtcPlatformPercentage}
        reserveHoldDays={reserveHoldDays}
        setReserveHoldDays={setReserveHoldDays}
      />
    </div>
  );
}

export default DepositTable;